import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FolderOpen, Folder, Layers, Search } from "lucide-react";
import { cn } from "@shadcn-in-nx/lib/utils";
import { PopoverContent } from "@shadcn-in-nx/ui/popover";
import { Input } from "@shadcn-in-nx/ui/input";

function WorkflowPopoverList({ workflows, selectedWorkflowId, onSelect, className }) {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");

    const filtered = useMemo(() => {
        if (!search.trim()) return workflows;
        const q = search.toLowerCase();
        return workflows.filter((w) => w.name.toLowerCase().includes(q));
    }, [workflows, search]);

    return (
        <PopoverContent
            className={cn("p-0", className)}
            align="start"
            side="bottom"
            collisionPadding={8}
        >
            <div className="p-2 border-b">
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("files.folders.searchPlaceholder")}
                        className="h-8 pl-8 text-sm"
                        autoFocus
                    />
                </div>
            </div>
            <div className="max-h-[240px] overflow-y-auto p-1">
                <button
                    onClick={() => onSelect(null)}
                    className={cn(
                        "w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm transition-colors text-left",
                        !selectedWorkflowId ? "bg-primary/10 text-primary font-medium" : "hover:bg-gray-100 text-gray-700"
                    )}
                >
                    <Layers className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{t("files.folders.all")}</span>
                </button>
                {filtered.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">
                        {t("files.folders.noResults")}
                    </p>
                ) : (
                    filtered.map((wf) => {
                        const isActive = selectedWorkflowId === wf.id;
                        return (
                            <button
                                key={wf.id}
                                onClick={() => onSelect(wf.id)}
                                className={cn(
                                    "w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm transition-colors text-left",
                                    isActive ? "bg-primary/10 text-primary font-medium" : "hover:bg-gray-100 text-gray-700"
                                )}
                            >
                                {isActive ? <FolderOpen className="h-4 w-4 shrink-0" /> : <Folder className="h-4 w-4 shrink-0" />}
                                <span className="truncate flex-1">{wf.name}</span>
                                <span className="text-xs text-gray-400">{wf.fileCount}</span>
                            </button>
                        );
                    })
                )}
            </div>
        </PopoverContent>
    );
}

export default WorkflowPopoverList;
